/**
 * main.tsx — the GPS Clinics portal composition root (browser entry).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign);
 * migrated from `<BrowserRouter>` to `createBrowserRouter` + `RouterProvider`
 * for the C1.8 navigation-resilience slice (feat/patient-journey-navigation-
 * resilience) — a DATA router is required for `useBlocker`
 * (`navigation/useUnsavedChangesGuard.ts`), which throws under the old
 * component-router (`react-router`'s "Picking a Router" guide).
 *
 * WHY this file exists:
 *   It is the ONE place that mounts the SPA. The brand fonts are SELF-HOSTED via
 *   @fontsource (no CDN — CSP-safe): Fraunces for headings, DM Sans for
 *   body/UI, bundled by Vite. The design tokens (light theme only) live in
 *   tokens.css. The route table (`routes.tsx`) is shared with `App.tsx`
 *   (the classic tree every existing spec mounts under a plain
 *   `MemoryRouter` — see `App.tsx`'s WHY block for the test-environment
 *   reason it stays that way) so the two mounting strategies can never drift
 *   apart. `AuthProvider` must live INSIDE the router (it calls `useNavigate`
 *   on a 401/logout), so it wraps the routed content via `RootLayout` rather
 *   than wrapping `RouterProvider` from outside. Excluded from coverage
 *   because it only mounts; all logic lives in tested modules.
 */
import '@fontsource/dm-sans/400.css';
import '@fontsource/dm-sans/500.css';
import '@fontsource/dm-sans/600.css';
import '@fontsource/dm-sans/700.css';
// Fraunces 300 is the journey's display weight (premium look, CTO 2026-09-24 —
// tokens.css P7). Self-hosted like every other face; no rule outside a journey
// root asks for a weight below 400, so it is inert everywhere else.
import '@fontsource/fraunces/300.css';
import '@fontsource/fraunces/400.css';
import '@fontsource/fraunces/600.css';
import './theme/tokens.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { BASENAME, comingSoonEnabled } from './config';
import { ComingSoon } from './coming-soon/ComingSoon';
import { routeTable } from './routes';
import { AppShell } from './ui/chrome';

/** The data router's single root layout: AuthProvider (needs router context) → AppShell chrome → the matched route. */
function RootLayout() {
  return (
    <AuthProvider>
      <AppShell>
        <Outlet />
      </AppShell>
    </AuthProvider>
  );
}

// `basename` is the ONE prefix this build is served under (Clinics-in-Core,
// 2026-09-07): '/' when the portal is served at its own origin root
// (app.clinics), '/clinics' when GPS Core fronts this same build under
// <core-host>/clinics/*. It is derived from Vite's `base` via BASENAME so the
// router, every raw `<a href>` and every absolute link the app mints agree on a
// single value — see config.ts. Standalone builds pass '/' exactly as before.
const router = createBrowserRouter([{ element: <RootLayout />, children: routeTable }], {
  basename: BASENAME || '/',
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found.');
}

// Launch bake (VITE_COMING_SOON=true, prod only): render the holding page ALONE.
// No <BrowserRouter>, no <AuthProvider>, no <App> — the router, session and every
// API call are bypassed, so the front door takes no real orders while the backend
// is verified. Remove the prod variable and redeploy to go live.
createRoot(rootEl).render(
  <StrictMode>
    {comingSoonEnabled() ? <ComingSoon /> : <RouterProvider router={router} />}
  </StrictMode>,
);
